import { useState, useMemo } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { searchOrgTree } from "@/lib/orgChartValidation";
import type { OrgNode } from "@/types";

interface OrgChartSearchProps {
  orgTree: OrgNode | null;
  departments: string[];
  onNodeSelect: (node: OrgNode) => void;
  onHighlightNodes: (nodePids: string[]) => void;
}

/**
 * Search and filter controls for org chart
 * Currently supports name search only (department filtering hidden for now)
 */
export function OrgChartSearch({
  orgTree,
  departments: _departments, // Hidden for now, will be used when department filter is re-enabled
  onNodeSelect,
  onHighlightNodes,
}: OrgChartSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  // const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]); // Hidden for now

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchOrgTree(orgTree, searchQuery);
  }, [orgTree, searchQuery]);

  // Department filter results - disabled for now
  // const departmentResults = useMemo(() => {
  //   if (selectedDepartments.length === 0) return [];
  //   return filterByDepartment(orgTree, selectedDepartments);
  // }, [orgTree, selectedDepartments]);

  // Results (only search for now)
  const combinedResults = searchResults;

  // Highlight matching nodes
  const handleHighlight = () => {
    const pids = combinedResults.map((n) => n.pid);
    onHighlightNodes(pids);
  };

  // Clear filters
  const handleClear = () => {
    setSearchQuery("");
    // setSelectedDepartments([]); // Hidden for now
    onHighlightNodes([]);
  };

  // Toggle department selection - disabled for now
  // const toggleDepartment = (dept: string) => {
  //   setSelectedDepartments((prev) =>
  //     prev.includes(dept)
  //       ? prev.filter((d) => d !== dept)
  //       : [...prev, dept]
  //   );
  // };

  const hasActiveFilters = searchQuery.trim().length > 0;

  return (
    <div className="absolute top-6 left-6 right-6 md:left-auto md:w-96 bg-background border rounded-lg shadow-lg p-4 z-10">
      {/* Search Input */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, email, or position..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 pr-9"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => setSearchQuery("")}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Department Filter - Hidden for now, will be added later */}
      {/* <div className="flex items-center gap-2 mb-3">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="w-full justify-start">
              <Filter className="mr-2 h-4 w-4" />
              Filter by Department
              {selectedDepartments.length > 0 && (
                <Badge variant="secondary" className="ml-auto">
                  {selectedDepartments.length}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64" align="start">
            <div className="space-y-2">
              <p className="text-sm font-medium">Departments</p>
              {departments.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No departments defined
                </p>
              ) : (
                departments.map((dept) => (
                  <div key={dept} className="flex items-center space-x-2">
                    <Checkbox
                      id={dept}
                      checked={selectedDepartments.includes(dept)}
                      onCheckedChange={() => toggleDepartment(dept)}
                    />
                    <Label
                      htmlFor={dept}
                      className="text-sm font-normal cursor-pointer flex-1"
                    >
                      {dept}
                    </Label>
                  </div>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div> */}

      {/* Results */}
      {hasActiveFilters && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {combinedResults.length} result{combinedResults.length !== 1 ? "s" : ""}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="h-auto py-1"
            >
              Clear
            </Button>
          </div>

          {combinedResults.length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-1 border-t pt-2">
              {combinedResults.slice(0, 10).map((node) => (
                <button
                  key={node.pid}
                  onClick={() => onNodeSelect(node)}
                  className="w-full text-left p-2 rounded hover:bg-muted transition-colors"
                >
                  <p className="text-sm font-medium">{node.fullName}</p>
                  <p className="text-xs text-muted-foreground">
                    {node.position || "No position"}
                    {node.department && ` • ${node.department}`}
                  </p>
                </button>
              ))}
              {combinedResults.length > 10 && (
                <p className="text-xs text-muted-foreground text-center py-2">
                  +{combinedResults.length - 10} more results
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
